import { useContext } from 'react';
import { find, map, values } from 'lodash';
import { saveField } from '../../utils';
import { setLastAction, websiteDisable } from '../data/actions';
import cogoToast from 'cogo-toast';
import { DispatchContext, StateContext } from '../../App';
import { useMutation } from 'graphql-hooks';
import { DeleteDraftMutation, DraftToProductionMutation, ExecuteMutation, SiteUpdateMutation } from '../data/graphql';
import { v4 } from 'uuid';

export const mapPages = (pagesData, isCopy = false, website_id = null) => {
  return map(values(pagesData), (page) => {
    return {
      id: !isCopy ? page.id : v4(),
      websiteId: website_id,
      path: page.url,
      typeName: page.type,
      category: page.category ? page.category : 'other',
      slug: JSON.stringify(page.slug),
      tags: page.tags,
      indexable: page.indexable,
      analyticsName: page.analyticsName,
      analyticsType: page.analyticsType,
      matchPath: page.matchPath,
      meta: map(page.meta, (meta, index) => {
        return {
          id: !isCopy ? meta.id : v4(),
          language: meta.language,
          title: meta.title,
          description: meta.description
        };
      }),
      properties: map(page.properties, (prop) => {
        return {
          id: !isCopy ? prop.id : v4(),
          type: prop.type,
          data: JSON.stringify(prop.data),
          language: prop.language
        };
      }),
      blocks: map(page.blocks, (block, index) => {
        return {
          id: !isCopy ? block.id : v4(),
          contentType: block.contentType,
          variant: block.componentName,
          order: index,
          content: map(block.content, (content) => {
            return {
              id: !isCopy ? content.id : v4(),
              culture: content.culture,
              fields: map(content.fields, (field) => {
                if (!isCopy) {
                  return {
                    name: field.name,
                    value: !isCopy ? saveField(field) : field.value
                  };
                } else {
                  return {
                    name: field.name,
                    type: field.type,
                    value: !isCopy ? saveField(field) : field.value
                  };
                }
              })
            };
          }),
          campaignId: block.campaignId,
          hideForCampaign: block.hideForCampaign,
          analyticsName: block.analyticsName
        };
      })
    };
  });
};

export function UseBuilderActions(language) {
  const state = useContext(StateContext);
  const dispatch = useContext(DispatchContext);
  const currentState = state.history[state.history.length - 1];
  const [saveMutation, { loading: saveLoading }] = useMutation(SiteUpdateMutation);
  const [convertDraftToProductionMutation] = useMutation(DraftToProductionMutation);
  const [deleteDraftMutation] = useMutation(DeleteDraftMutation);
  const [executeMutation, { loading: executeLoading }] = useMutation(ExecuteMutation);

  const updateWebsite = () => {
    const currentWebsite = find(state.websites, (w) => w.id === state.currentWebsite);
    const pagesData = mapPages(currentState.pages, false, currentWebsite.id);

    const customBlocksData = map(currentState.customBlocks, (block) => {
      return {
        id: block.name,
        description: block.description,
        content: block.content
      };
    });

    // const campaignsData = map(currentState.campaigns, (campaign) => {
    //   return {
    //     uuid: campaign.id,
    //     name: campaign.name,
    //     start: dayjs(campaign.start).format('YYYY-MM-DD'),
    //     stop: dayjs(campaign.stop).format('YYYY-MM-DD'),
    //     isDraft: campaign.isDraft,
    //     disabled: campaign.disabled | false
    //   };
    // });

    // const advertisementGroupsData = map(currentState.advertisementGroups, (group) => {
    //   return {
    //     id: group.id,
    //     name: group.name,
    //     isDraft: group.isDraft,
    //     disabled: group.disabled
    //   };
    // });

    // const advertisementsData = map(currentState.advertisements, (advertisement) => {
    //   return {
    //     id: advertisement.id,
    //     description: advertisement.description,
    //     content: advertisement.content,
    //     groups: advertisement.groups,
    //     isDraft: advertisement.isDraft,
    //     disabled: advertisement.disabled,
    //     filters: JSON.stringify(advertisement.filters)
    //   };
    // });

    // const vehicleSearchFiltersData = map(currentState.vehicleSearchFilters, (f) => {
    //   return {
    //     id: f.id,
    //     name: JSON.stringify(f.name),
    //     type: f.type,
    //     filters: JSON.stringify(f.filters),
    //     isDraft: f.isDraft,
    //     disabled: f.disabled
    //   };
    // });

    const pageContent = map(currentState.pageContentSEO, (content) => {
      return {
        ...content,
        filters: JSON.stringify(content.filters),
        text: JSON.stringify(content.text)
      };
    });

    saveMutation({
      variables: {
        data: {
          website: {
            uuid: currentWebsite.id,
            name: currentWebsite.name,
            pages: pagesData
          },

          pageContent: pageContent,
          customBlocks: customBlocksData
          //campaigns: campaignsData,

          //advertisementGroups: advertisementGroupsData,
          //advertisements: advertisementsData,
          //vehicleSearchFilters: vehicleSearchFiltersData
        }
      }
    })
      .then((res) => {
        const { ok } = res.data.siteUpdate;
        if (ok) {
          setLastAction(dispatch);
          cogoToast.success('Layout saved!');
        }
      })
      .catch((error) => {
        cogoToast.error('Error saving layout!');
      });
  };

  const executeScript = (action, project, payload = {}) => {
    executeMutation({
      variables: {
        action: action,
        project: project,
        payload: JSON.stringify(payload)
      }
    }).then((r) => {
      const { execute } = r.data;
      if (execute.ok) {
        setLastAction(dispatch);
      } else {
        cogoToast.error('Error!');
      }
    });
  };

  const convertDraftToProduction = (websiteId) => {
    convertDraftToProductionMutation({
      variables: {
        website: websiteId
      }
    }).then((result) => {
      const { draftToProduction } = result.data;
      if (draftToProduction.ok) {
        executeScript('release', 'website', {});
      }
    });
  };

  const draftDelete = (websiteId) => {
    deleteDraftMutation({
      variables: {
        website: websiteId
      }
    }).then((result) => {
      websiteDisable(dispatch, websiteId);
    });
  };

  return {
    dispatch,
    updateWebsite,
    convertDraftToProduction,
    draftDelete,
    executeScript,
    loading: saveLoading || executeLoading
  };
}

export default UseBuilderActions;
